import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  #lastPointerType = '';

  /** @type {HTMLElement | null} */
  #touchOpenedItem = null;

  /** @type {Map<ReturnType<typeof setTimeout>, HTMLElement>} */
  #animationTimeouts = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#bindListeners();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bindListeners();
  }

  #bindListeners() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    // A plain list keeps primary navigation visible without the optional overflow surface.
    const container = this.overflowMenu ?? this.refs.overflowMenu;
    container?.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'touch') this.#debouncedDeactivate();
    }, { signal });
    document.addEventListener('pointerdown', this.#handlePointerDown, { capture: true, signal });
    this.addEventListener('click', this.#handleTouchClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });

    if (document.readyState === 'complete') {
      this.#preloadImages();
    } else {
      window.addEventListener('load', this.#preloadImages, { once: true, signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    this.#deactivate(this.#state.activeItem, true);
    for (const [timer, item] of this.#animationTimeouts) {
      clearTimeout(timer);
      item.removeAttribute('data-animating');
    }
    this.#animationTimeouts.clear();
    this.#touchOpenedItem = null;
    this.#lastPointerType = '';
  }

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    this.#lastPointerType = event.pointerType;
    if (!(event.target instanceof Node)) return;
    if (this.#touchOpenedItem && !this.contains(event.target)) {
      this.#debouncedActivateHandler.cancel();
      this.#debouncedDeactivate.cancel();
      this.#deactivate(this.#state.activeItem, true);
      this.#touchOpenedItem = null;
    } else if (event.pointerType !== 'touch') {
      this.#touchOpenedItem = null;
    }
  };

  /** @param {MouseEvent} event */
  #handleTouchClick = (event) => {
    if (this.#lastPointerType !== 'touch' || event.detail === 0 || event.defaultPrevented) return;
    if (event.button !== 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (!(event.target instanceof Element)) return;
    const item = event.target.closest('a[ref="menuitem"][aria-haspopup="true"]');
    if (!(item instanceof HTMLElement) || !this.contains(item)) return;
    // Focus or synthesized hover can open a menu before its first touch click.
    // Only a previous touch click on this same open parent permits navigation.
    if (this.#touchOpenedItem === item && this.#state.activeItem === item) return;
    event.preventDefault();
    this.#touchOpenedItem = item;
    this.#debouncedActivateHandler.cancel();
    this.#debouncedDeactivate.cancel();
    this.#activateHandler(event, item);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    this.#touchOpenedItem = null;
    const item = this.#state.activeItem;
    if (event.key !== 'Escape' || !item) return;
    event.preventDefault();
    event.stopPropagation();
    this.#debouncedDeactivate.cancel();
    item.focus({ preventScroll: true });
    // Focus restoration routes through /activate; cancel it before closing.
    this.#debouncedActivateHandler.cancel();
    this.#deactivate(item, true);
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if ('pointerType' in event && event.pointerType !== 'touch') this.#touchOpenedItem = null;
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent | MouseEvent} event
   * @param {HTMLElement | null} [touchItem] - Parent link resolved from a touch click.
   */
  #activateHandler = (event, touchItem = null) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    const target = touchItem?.parentElement ?? event.target;
    if (!(target instanceof Element)) return;

    let item = touchItem ?? findMenuItem(target);

    if (!item || item == this.#state.activeItem) return;
    if (this.#touchOpenedItem !== item) this.#touchOpenedItem = null;

    const isDefaultSlot = target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    // Touch pointerleave means the finger lifted, not that the shopper left the menu.
    if ('pointerType' in event && event.pointerType === 'touch') return;
    if (this.#touchOpenedItem && event instanceof FocusEvent && !this.contains(event.relatedTarget)) {
      this.#debouncedActivateHandler.cancel();
      this.#debouncedDeactivate.cancel();
      this.#deactivate(this.#state.activeItem, true);
      return;
    }
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force] - Close even while the pointer remains over the menu.
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;
    if (!force && this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.#touchOpenedItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    const timer = setTimeout(() => {
      item.removeAttribute('data-animating');
      this.#animationTimeouts.delete(timer);
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
    this.#animationTimeouts.set(timer, item);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
